import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { defineFormPage, useFormPage } from '@repo/form';
import { FormActions } from '@repo/web-form';
import { FormPageLayout } from '../../../../components/layout/FormPageLayout';
import { Input, FormField } from '../../../../components/ui/FormField';
import { useToast } from '../../../../components/ui/Toast';
import { z } from 'zod';
import { Banner } from '../../../../components/ui/Banner';
import { useRegisterDevice } from '../../../../features/settings/network/devices.api';

export const Route = createFileRoute('/_auth/settings/devices/new')({
  component: RegisterDevicePage,
});

const deviceSchema = z.object({
  fingerprint: z.string().min(1, 'Device identifier is required'),
  label: z.string().min(1, 'Device label is required'),
});

type DeviceForm = z.infer<typeof deviceSchema>;

const deviceFormConfig = defineFormPage<DeviceForm>({
  resource: 'devices',
  defaultValues: { fingerprint: '', label: '' },
  schema: deviceSchema,
});

function RegisterDevicePage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const registerDeviceMutation = useRegisterDevice();

  const {
    form,
    isSubmitting,
    handleSubmit,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(deviceFormConfig, { mutateAsync: (values) => registerDeviceMutation.mutateAsync(values) }, {
    routeId: 'settings/devices/new',
    onSuccess: () => {
      toast({
        type: 'success',
        title: 'Device Registered',
        description: 'The collector tablet fingerprint has been white-listed on the secure gateway.',
      });
      void navigate({ to: '/settings/devices' });
    },
    onError: (err: { message?: string }) => {
      toast({ type: 'error', title: 'Error', description: err.message || 'Failed to register device.' });
    },
  });

  return (
    <FormPageLayout
      title="Register Authorized Device"
      subtitle="Register an authorized physical Android tablet or mobile device. Only registered devices can log in to collection apps."
      backTo="/settings/devices"
      variant="card"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
            {hasDraft && (
              <Banner
                variant="info"
                title="Restore Draft"
                description="You have an unsaved draft from a previous session. Would you like to restore it?"
                action={{
                  label: 'Restore',
                  onClick: () => {
                    const draft = restoreDraft();
                    if (draft) {
                      Object.entries(draft).forEach(([key, val]) => {
                        form.setFieldValue(key as never, val as never);
                      });
                    }
                    dismissDraft();
                  },
                }}
                secondaryAction={{
                  label: 'Discard',
                  onClick: () => {
                    clearDraft();
                  },
                }}
              />
            )}

            <form.Field
              name="fingerprint"
              validators={{ onChange: ({ value }: { value: string }) => deviceSchema.shape.fingerprint.safeParse(value).success ? undefined : deviceSchema.shape.fingerprint.safeParse(value).error?.issues[0]?.message }}
            >
              {(field: any) => (
                <FormField label="Unique Device Identifier (IMEI or Device Code)" required>
                  <Input
                    placeholder="e.g. TAB-772-HY8"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormField>
              )}
            </form.Field>

            <form.Field
              name="label"
              validators={{ onChange: ({ value }: { value: string }) => deviceSchema.shape.label.safeParse(value).success ? undefined : deviceSchema.shape.label.safeParse(value).error?.issues[0]?.message }}
            >
              {(field: any) => (
                <FormField label="Device Nickname / Label" required>
                  <Input
                    placeholder="e.g. Lenovo Tab M10 - Collection #4"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormField>
              )}
            </form.Field>

            <FormActions onCancel={() => void navigate({ to: '/settings/devices' })} isSubmitting={isSubmitting} submitLabel="Register Device" />
          </form>
    </FormPageLayout>
  );
}
